import { Link, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { Card, Skeleton } from '@tatra/ui';
import {
  CalendarCheck,
  CheckCircle2,
  ChevronRight,
  Circle,
  ClipboardList,
  DoorOpen,
  UserPlus,
} from 'lucide-react';
import { Slot } from '../../extensions/runtime.js';
import { useAuth } from '../../lib/auth.js';
import { AREAS, visibleNav } from '../../lib/nav.js';
import { quickAdd } from '../../lib/quick-add.js';
import { useMembers, useTeams } from '../admin/api.js';
import { AttentionCard } from './AttentionCard.js';

// The day's work is fetched with its own chunk, so the first route stays inside the size budget.
const TodayPanel = lazy(() => import('../leads/TodayPanel.js').then((m) => ({ default: m.TodayPanel })));
// The role dashboard (M11, D-139) likewise.
const RoleDashboard = lazy(() =>
  import('../dashboards/RoleDashboard.js').then((m) => ({ default: m.RoleDashboard })),
);

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/**
 * Today (D-123): what to do next, one tap away — new lead, walk-in, site visit, task — then the day's
 * calls, follow-ups and visits, then every part of the CRM the member uses.
 */
export function HomePage() {
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const isAdmin = can('org.member.create');
  const members = useMembers();
  const teams = useTeams();
  const nav = visibleNav(can);

  const checklist = [
    {
      done: !!me?.user.twoFactorEnabled,
      label: 'Turn on 2-step verification for your account',
      to: '/profile',
    },
    { done: (teams.data?.length ?? 0) > 0, label: 'Create your sales teams', to: '/admin/teams' },
    { done: (members.data?.length ?? 0) > 1, label: 'Invite your team', to: '/admin/members' },
  ];
  const setupLeft = checklist.filter((c) => !c.done).length;

  const actions = [
    {
      key: 'lead',
      label: 'New lead',
      icon: UserPlus,
      allowed: can('leads.lead.create'),
      run: () => quickAdd.open('lead'),
    },
    {
      key: 'walk-in',
      label: 'Walk-in',
      icon: DoorOpen,
      allowed: can('leads.lead.create'),
      run: () => void navigate({ to: '/walk-in' }),
    },
    {
      key: 'visit',
      label: 'Book a visit',
      icon: CalendarCheck,
      allowed: can('activity.visit.manage'),
      run: () => quickAdd.open('visit'),
    },
    {
      key: 'task',
      label: 'Add a task',
      icon: ClipboardList,
      allowed: can('activity.task.manage'),
      run: () => quickAdd.open('task'),
    },
  ].filter((a) => a.allowed);

  const areas = AREAS.filter((a) => a.key !== 'today')
    .map((a) => {
      const pages = nav.filter((n) => n.area === a.key && !n.hidden);
      return { ...a, to: a.key === 'settings' ? '/admin' : pages[0]?.to, pages };
    })
    .filter((a) => a.pages.length > 0 && a.to);

  const today = new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(),
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <h1 className="font-display text-[1.75rem] leading-tight font-semibold tracking-[-0.02em] text-text sm:text-[2rem]">
            {greeting()}, {me?.member.displayName.split(' ')[0] ?? ''}
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            {today} · <span>{me?.role.name}</span>
          </p>
        </div>
      </header>

      <AttentionCard />

      {actions.length > 0 && (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4" data-testid="today-actions">
          {actions.map((a, i) => (
            <button
              key={a.key}
              type="button"
              onClick={a.run}
              className={
                i === 0
                  ? 'group flex min-h-16 items-center gap-3 rounded-2xl bg-primary px-4 text-left text-sm font-semibold text-primary-fg shadow-[0_10px_28px_-12px_rgb(236_31_156/0.8)] transition-[background-color,transform] duration-150 hover:bg-primary-hover active:scale-[0.98]'
                  : 'group flex min-h-16 items-center gap-3 rounded-2xl border border-border bg-surface px-4 text-left text-sm font-semibold text-text shadow-soft transition-[border-color,transform] duration-150 hover:border-primary/40 active:scale-[0.98]'
              }
            >
              <span
                className={
                  i === 0
                    ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15'
                    : 'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary'
                }
              >
                <a.icon className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              {a.label}
            </button>
          ))}
        </div>
      )}

      {can('analytics.dashboard.read') && (
        <Suspense fallback={<Skeleton className="h-48 rounded-2xl" />}>
          <RoleDashboard />
        </Suspense>
      )}

      {can('activity.task.read') && (
        <Suspense fallback={<Skeleton className="h-48 rounded-2xl" />}>
          <TodayPanel />
        </Suspense>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <section
          className={isAdmin ? 'min-w-0 lg:col-span-2' : 'min-w-0 lg:col-span-3'}
          aria-labelledby="go-to"
        >
          <h2 id="go-to" className="mb-3 font-display text-lg font-semibold text-text">
            Everything else
          </h2>
          <ul
            className={
              isAdmin
                ? 'grid grid-cols-1 gap-2.5 sm:grid-cols-2'
                : 'grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4'
            }
          >
            {areas.map((a) => (
              <li key={a.key} className="min-w-0">
                <Link
                  to={a.to!}
                  className="group flex min-h-16 items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 shadow-soft transition-colors hover:border-primary/40"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bg text-text-muted transition-colors group-hover:bg-primary-soft group-hover:text-primary">
                    <a.icon className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-text">
                      {a.pages.length === 1 && a.key !== 'settings' ? a.pages[0]!.label : a.label}
                    </span>
                    {a.pages.length > 1 && a.key !== 'settings' && (
                      <span className="block truncate text-xs text-text-muted">
                        {a.pages.map((p) => p.tab ?? p.label).join(' · ')}
                      </span>
                    )}
                  </span>
                  <ChevronRight
                    className="h-4 w-4 shrink-0 text-text-faint transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {isAdmin && (
          <Card className="p-5">
            <h2 className="font-display text-lg font-semibold text-text">Set up your CRM</h2>
            <p className="mb-3 text-sm text-text-muted">
              {setupLeft === 0 ? 'All done' : `${setupLeft} of ${checklist.length} left`}
            </p>
            <ul className="space-y-1">
              {checklist.map((c) => (
                <li key={c.label}>
                  <Link
                    to={c.to}
                    className="flex min-h-11 items-center gap-3 rounded-lg px-2 text-sm text-text hover:bg-surface-hover"
                  >
                    {c.done ? (
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-label="Done" />
                    ) : (
                      <Circle className="h-5 w-5 shrink-0 text-text-faint" aria-label="To do" />
                    )}
                    <span className={c.done ? 'text-text-muted line-through' : ''}>{c.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
      <Slot name="home.widgets" props={{}} className="grid gap-4 lg:grid-cols-3" />
    </div>
  );
}
